/**
 * Product 3D Customizer Web Component
 * Manages 3D interactive viewer, color synchronization, equipment swapping,
 * non-3D fallback, dynamic price calculation and sticky add to cart.
 */

if (!customElements.get('product-3d-customizer')) {
  class Product3DCustomizer extends HTMLElement {
    constructor() {
      super();
      this.has3D = this.getAttribute('data-has-3d') === 'true' || this.dataset['has-3d'] === 'true' || !!this.querySelector('model-viewer');
      this.modelViewer = this.querySelector('model-viewer');
      this.loadingOverlay = this.querySelector('.product-3d__loading-overlay');
      this.viewportWrapper = this.querySelector('.product-3d__viewport-wrapper');
      this.galleryWrapper = this.querySelector('.product-3d__gallery-wrapper');
      this.form = this.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
      this.mainAddBtn = this.querySelector('.product-3d__add-to-cart');
      this.stickyBar = this.querySelector('.product-3d__sticky-bar');

      // Price elements
      this.priceCurrent = this.querySelector('[data-price-current]');
      this.priceCompare = this.querySelector('[data-price-compare]');
      this.priceBadge = this.querySelector('[data-price-badge]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
      this.comparePrice = parseInt(this.dataset.comparePrice || '0', 10);
      this.currencySymbol = this.dataset.currencySymbol || '€';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} €';

      // State
      this.currentColor = '';
      this.currentHex = '';
      this.activeEquipment = null;
      this.autoRotateActive = false;

      // Target material name from block setting if specified
      const pickerEl = this.querySelector('[data-target-material]');
      this.targetMaterialName = pickerEl ? (pickerEl.dataset.targetMaterial || '') : '';

      // Base 3D model sources
      this.baseModelSrc = this.dataset.initialModel || '';
      this.baseIosSrc = this.dataset.initialIosModel || '';

      if (this.modelViewer) {
        this.initialCameraOrbit = this.modelViewer.getAttribute('camera-orbit') || '0deg 75deg 105%';
        this.initialCameraTarget = this.modelViewer.getAttribute('camera-target') || 'auto auto auto';
        this.initialFov = this.modelViewer.getAttribute('field-of-view') || 'auto';
      }

      this.init();
    }

    init() {
      this.initTabs();
      this.initControls();
      this.initDrawerWatcher();
      this.initSwatches();
      this.initEquipments();
      this.initQuantity();
      this.initThumbnails();
      this.initStickyCart();
      this.initDeliveryEstimator();
      this.initUrgencyDeadlines();
      this.initLooxTriggers();
      this.updateTotalPrice();

      // Listen for model load finish
      if (this.modelViewer) {
        this.modelViewer.addEventListener('load', () => {
          this.hideLoading();

          // Log detected materials for easy setup in theme customizer
          if (this.modelViewer.model && this.modelViewer.model.materials) {
            const matNames = this.modelViewer.model.materials.map(m => m.name || 'Sans nom');
            console.log('[3D Customizer] Matériaux détectés dans le fichier 3D:', matNames);
            if (this.modelViewer.availableVariants && this.modelViewer.availableVariants.length > 0) {
              console.log('[3D Customizer] Variantes KHR détectées dans le fichier 3D:', this.modelViewer.availableVariants);
            }
          }

          this.applyColorToMesh(this.currentColor, this.currentHex);
        });
        this.modelViewer.addEventListener('error', (e) => {
          console.warn('Model viewer load error:', e);
          this.hideLoading();
        });
      }
    }

    // =========================================================================
    // 3D VIEWPORT CONTROLS
    // =========================================================================
    initControls() {
      const rotateBtn = this.querySelector('[data-action="toggle-rotate"]');
      if (rotateBtn && this.modelViewer) {
        rotateBtn.addEventListener('click', () => {
          this.autoRotateActive = !this.autoRotateActive;
          if (this.autoRotateActive) {
            this.modelViewer.setAttribute('auto-rotate', '');
            rotateBtn.classList.add('is-active');
          } else {
            this.modelViewer.removeAttribute('auto-rotate');
            rotateBtn.classList.remove('is-active');
          }
        });
      }

      const resetBtn = this.querySelector('[data-action="reset-view"]');
      if (resetBtn && this.modelViewer) {
        resetBtn.addEventListener('click', () => {
          const orbit = this.initialCameraOrbit || '0deg 75deg 105%';
          const target = this.initialCameraTarget || 'auto auto auto';
          const fov = this.initialFov || 'auto';

          this.modelViewer.cameraOrbit = orbit;
          this.modelViewer.cameraTarget = target;
          this.modelViewer.fieldOfView = fov;

          if (typeof this.modelViewer.jumpCameraToGoal === 'function') {
            this.modelViewer.jumpCameraToGoal();
          }
          if (typeof this.modelViewer.resetTurntableRotation === 'function') {
            this.modelViewer.resetTurntableRotation();
          }

          // Visual feedback on refresh button
          resetBtn.style.transition = 'transform 0.4s ease';
          resetBtn.style.transform = 'rotate(-360deg)';
          setTimeout(() => {
            resetBtn.style.transition = '';
            resetBtn.style.transform = '';
          }, 400);
        });
      }

      const fullscreenBtn = this.querySelector('[data-action="toggle-fullscreen"]');
      if (fullscreenBtn && this.viewportWrapper) {
        fullscreenBtn.addEventListener('click', () => {
          if (!document.fullscreenElement) {
            this.viewportWrapper.requestFullscreen().catch(err => console.log(err));
          } else {
            document.exitFullscreen();
          }
        });
      }

      const arBtn = this.querySelector('[data-action="activate-ar"]');
      if (arBtn && this.modelViewer) {
        arBtn.addEventListener('click', () => {
          if (this.modelViewer.canActivateAR) {
            this.modelViewer.activateAR();
          } else {
            alert("La réalité augmentée nécessite un smartphone compatible (iOS Safari avec QuickLook ou Android avec Google Play Services pour AR).");
          }
        });
      }
    }

    // =========================================================================
    // DRAWER / MOBILE MENU WATCHER (Zero CPU overhead, no MutationObserver loop)
    // =========================================================================
    initDrawerWatcher() {
      const headerDrawer = document.querySelector('header-drawer');
      if (headerDrawer) {
        headerDrawer.addEventListener('click', () => {
          setTimeout(() => {
            const isOpen = headerDrawer.querySelector('details[open]') !== null;
            if (isOpen) {
              this.classList.add('product-3d-customizer--drawer-open');
            } else {
              this.classList.remove('product-3d-customizer--drawer-open');
            }
          }, 50);
        });
      }

      document.addEventListener('header-drawer:open', () => {
        this.classList.add('product-3d-customizer--drawer-open');
      });
      document.addEventListener('header-drawer:close', () => {
        this.classList.remove('product-3d-customizer--drawer-open');
      });
      document.addEventListener('cart-drawer:open', () => {
        this.classList.add('product-3d-customizer--drawer-open');
      });
      document.addEventListener('cart-drawer:close', () => {
        this.classList.remove('product-3d-customizer--drawer-open');
      });
    }

    // =========================================================================
    // TABS: 3D 360° vs PHOTOS
    // =========================================================================
    initTabs() {
      const tabBtns = this.querySelectorAll('[data-tab-mode]');
      if (!tabBtns.length) return;

      tabBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset.tabMode;
          tabBtns.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          if (mode === '3d') {
            if (this.viewportWrapper) this.viewportWrapper.style.display = 'flex';
            if (this.galleryWrapper) this.galleryWrapper.style.display = 'none';
          } else {
            if (this.viewportWrapper) this.viewportWrapper.style.display = 'none';
            if (this.galleryWrapper) this.galleryWrapper.style.display = 'flex';
          }
        });
      });
    }

    // =========================================================================
    // COLOR SWATCHES & 3D SYNCHRONIZATION
    // =========================================================================
    initSwatches() {
      const swatchBtns = this.querySelectorAll('[data-swatch-value]');
      const colorLabel = this.querySelector('[data-selected-color-name]');
      const stickyColorText = this.querySelector('[data-sticky-color]');
      const swatchesTrack = this.querySelector('.product-3d__swatches');

      let isDragging = false;

      // 1. Auto-correct swatches with fallback #FAF5F0 if JS can detect actual color name
      swatchBtns.forEach(btn => {
        const colorName = btn.dataset.colorName || btn.dataset.swatchValue;
        let hexColor = (btn.dataset.hexColor || '').toLowerCase();

        if (!hexColor || hexColor === '#faf5f0') {
          const detected = this.detectColorRgb(colorName) || this.cssColorToRgb(colorName);
          if (detected) {
            const newHex = this.rgbToHex(detected.r, detected.g, detected.b);
            btn.dataset.hexColor = newHex;
            const dot = btn.querySelector('.product-3d__swatch-color');
            if (dot) {
              dot.style.backgroundColor = newHex;
            }
          }
        }
      });

      swatchBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          if (isDragging) return;

          const colorName = btn.dataset.colorName || btn.dataset.swatchValue;
          const hexColor = btn.dataset.hexColor || '';
          console.log(`[3D Customizer] Clic sur couleur "${colorName}" (${hexColor})`);

          swatchBtns.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Smoothly scroll active swatch into view
          if (typeof btn.scrollIntoView === 'function') {
            btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }

          const variantId = btn.dataset.variantId;
          const variantPrice = parseInt(btn.dataset.variantPrice || '0', 10);
          const variantComparePrice = parseInt(btn.dataset.variantComparePrice || '0', 10);
          const colorModel = btn.dataset.modelUrl;
          const colorIosModel = btn.dataset.iosModelUrl;
          const variantImage = btn.dataset.variantImage;
          const mediaId = btn.dataset.mediaId;

          this.currentColor = colorName;
          this.currentHex = hexColor;
          if (colorLabel) colorLabel.textContent = colorName;
          if (stickyColorText) stickyColorText.textContent = colorName;

          // Update main form variant input
          if (variantId && this.form) {
            const idInput = this.form.querySelector('input[name="id"]');
            if (idInput) idInput.value = variantId;
          }

          // Update base price if variant price exists
          if (variantPrice > 0) {
            this.basePrice = variantPrice;
            this.comparePrice = variantComparePrice;
            this.updateTotalPrice();
          }

          // Update browser URL ?variant=
          if (variantId) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', variantId);
              window.history.replaceState({}, '', url.toString());
            } catch (e) {
              console.log(e);
            }
          }

          // Update 2D photo gallery image (non-3D product or Photos tab)
          this.updateGalleryImage(variantImage, mediaId);

          // Update 3D Model for Color
          this.sync3DColor(colorName, hexColor, colorModel, colorIosModel);
        });
      });

      // Mouse drag scroll support on desktop (with threshold to never block clicks)
      const attachDragScroll = (track) => {
        if (!track) return;
        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        track.addEventListener('mousedown', (e) => {
          isDown = true;
          isDragging = false;
          startX = e.pageX - track.offsetLeft;
          scrollLeft = track.scrollLeft;
        });

        window.addEventListener('mouseup', () => {
          if (isDown) {
            isDown = false;
            track.style.cursor = '';
            setTimeout(() => {
              isDragging = false;
            }, 80);
          }
        });

        track.addEventListener('mouseleave', () => {
          if (isDown) {
            isDown = false;
            track.style.cursor = '';
            setTimeout(() => {
              isDragging = false;
            }, 80);
          }
        });

        track.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const x = e.pageX - track.offsetLeft;
          const walk = x - startX;
          if (Math.abs(walk) > 6) {
            isDragging = true;
            e.preventDefault();
            track.style.cursor = 'grabbing';
            track.scrollLeft = scrollLeft - walk * 1.2;
          }
        });
      };

      if (swatchesTrack) attachDragScroll(swatchesTrack);
      this.querySelectorAll('.product-3d__pills').forEach(pillsTrack => attachDragScroll(pillsTrack));

      // Set initial color from first active swatch & center it
      const activeSwatch = this.querySelector('[data-swatch-value].is-active');
      if (activeSwatch) {
        this.currentColor = activeSwatch.dataset.colorName || activeSwatch.dataset.swatchValue;
        this.currentHex = activeSwatch.dataset.hexColor || '';
        setTimeout(() => {
          if (typeof activeSwatch.scrollIntoView === 'function') {
            activeSwatch.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }, 150);
      }

      // Support for standard pills options (in case options are rendered as pills)
      const pillBtns = this.querySelectorAll('[data-pill-value]');
      pillBtns.forEach(pill => {
        pill.addEventListener('click', () => {
          if (isDragging) return;
          const parent = pill.closest('.product-3d__pills');
          if (parent) {
            parent.querySelectorAll('.product-3d__pill-btn').forEach(p => p.classList.remove('is-active'));
          }
          pill.classList.add('is-active');

          const optionGroup = pill.closest('.product-3d__option-group');
          if (optionGroup) {
            const pillLabel = optionGroup.querySelector('[data-selected-pill-name]');
            if (pillLabel) pillLabel.textContent = pill.dataset.pillValue;
          }

          if (typeof pill.scrollIntoView === 'function') {
            pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }

          const variantId = pill.dataset.variantId;
          const variantPrice = parseInt(pill.dataset.variantPrice || '0', 10);
          const variantComparePrice = parseInt(pill.dataset.variantComparePrice || '0', 10);
          const colorName = pill.dataset.colorName || pill.dataset.pillValue;
          const variantImage = pill.dataset.variantImage;
          const mediaId = pill.dataset.mediaId;

          if (variantId && this.form) {
            const idInput = this.form.querySelector('input[name="id"]');
            if (idInput) idInput.value = variantId;
          }

          if (variantPrice > 0) {
            this.basePrice = variantPrice;
            this.comparePrice = variantComparePrice;
            this.updateTotalPrice();
          }

          if (variantId) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', variantId);
              window.history.replaceState({}, '', url.toString());
            } catch (e) {
              console.log(e);
            }
          }

          this.updateGalleryImage(variantImage, mediaId);

          this.currentColor = colorName;
          this.sync3DColor(colorName, '', '', '');
        });
      });
    }


    // =========================================================================
    // SYNCHRONISATION IMAGE PRINCIPALE GALERIE 2D
    // =========================================================================
    updateGalleryImage(imgSrc, mediaId) {
      if (!imgSrc && !mediaId) return;

      const mainImg = this.querySelector('.product-3d__gallery-main img');
      if (mainImg && imgSrc) {
        mainImg.style.opacity = '0.35';
        mainImg.style.transition = 'opacity 0.2s ease';
        const temp = new Image();
        temp.onload = () => {
          mainImg.src = imgSrc;
          mainImg.style.opacity = '1';
        };
        temp.src = imgSrc;
      }

      // Synchroniser la miniature correspondante dans la bande de vignettes
      const thumbBtns = this.querySelectorAll('.product-3d__thumb-btn');
      if (thumbBtns.length > 0) {
        thumbBtns.forEach(thumb => {
          if (mediaId && thumb.dataset.thumbTarget === String(mediaId)) {
            thumb.classList.add('is-active');
            if (typeof thumb.scrollIntoView === 'function') {
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          } else if (imgSrc && thumb.dataset.imgSrc === imgSrc) {
            thumb.classList.add('is-active');
            if (typeof thumb.scrollIntoView === 'function') {
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          } else {
            thumb.classList.remove('is-active');
          }
        });
      }
    }

    sync3DColor(colorName, hexColor, specificModelUrl, specificIosUrl) {
      if (!this.has3D || !this.modelViewer) return;

      // Priority 1: If an equipment is active and has an equipment model, keep equipment model
      if (this.activeEquipment && this.activeEquipment.model) {
        // Equipment model is currently loaded; apply color tint to it
        this.applyColorToMesh(colorName, hexColor);
        return;
      }

      // Priority 2: Dedicated GLB file for this color
      if (specificModelUrl) {
        this.loadNew3DModel(specificModelUrl, specificIosUrl);
        return;
      }

      // Priority 3: Check KHR_materials_variants in current model
      if (this.modelViewer.availableVariants && this.modelViewer.availableVariants.length > 0) {
        const matchingVariant = this.modelViewer.availableVariants.find(
          v => v.toLowerCase().trim() === colorName.toLowerCase().trim()
        );
        if (matchingVariant) {
          this.modelViewer.variantName = matchingVariant;
          return;
        }
      }

      // Priority 4: Dynamic material color tinting fallback
      this.applyColorToMesh(colorName, hexColor);
    }

    applyColorToMesh(colorName, hexColor) {
      if (!this.modelViewer || !this.modelViewer.model) return;

      // Smart color resolution: priority to real hex, then name detection, then fallback
      let rgb = null;
      if (hexColor && hexColor.toLowerCase() !== '#faf5f0') {
        rgb = this.hexToRgb(hexColor);
      }
      if (!rgb && colorName) {
        rgb = this.detectColorRgb(colorName) || this.cssColorToRgb(colorName);
      }
      if (!rgb && hexColor) {
        rgb = this.hexToRgb(hexColor);
      }
      if (!rgb) {
        console.warn(`[3D Customizer] Impossible d'extraire la couleur pour "${colorName}" (${hexColor})`);
        return;
      }

      const materials = this.modelViewer.model.materials;
      if (!materials || !materials.length) return;

      // Convert RGB 0-255 to 0-1 float range for glTF PBR standard
      const r = rgb.r / 255;
      const g = rgb.g / 255;
      const b = rgb.b / 255;
      const cssColor = this.rgbToHex(rgb.r, rgb.g, rgb.b);

      console.log(`[3D Customizer] Teinte demandée : "${colorName}" -> Hex: ${cssColor} -> RGB: [${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)}]`);

      const pickerEl = this.querySelector('[data-target-material]');
      const rawTarget = pickerEl ? (pickerEl.dataset.targetMaterial || '').trim() : (this.targetMaterialName || '').trim();
      const forceSolid = pickerEl ? (pickerEl.dataset.forceSolidColor !== 'false') : true;

      const targetList = rawTarget
        ? rawTarget.split(/[,;|]+/).map(t => t.trim().toLowerCase()).filter(Boolean)
        : [];

      let matchedCount = 0;

      const applyMatColor = (mat) => {
        if (!mat.pbrMetallicRoughness) return;

        // 1. If material has an image texture that blocks color changes, detach it
        if (forceSolid && mat.pbrMetallicRoughness.baseColorTexture) {
          try {
            mat.pbrMetallicRoughness.baseColorTexture.setTexture(null);
          } catch(err) {
            console.log(err);
          }
        }

        // 2. Apply clean color factor (float array [r, g, b, 1.0] is glTF PBR standard)
        try {
          mat.pbrMetallicRoughness.setBaseColorFactor([r, g, b, 1.0]);
        } catch (e) {
          try {
            mat.pbrMetallicRoughness.setBaseColorFactor(cssColor);
          } catch(err2) {
            console.error('[3D Customizer] Erreur setBaseColorFactor:', err2);
          }
        }

        // 3. Normalize specular highlights so fabrics do not shine like chrome/metal
        if (typeof mat.pbrMetallicRoughness.setMetallicFactor === 'function') {
          if (mat.pbrMetallicRoughness.metallicFactor > 0.05) {
            mat.pbrMetallicRoughness.setMetallicFactor(0.0);
          }
        }
        if (typeof mat.pbrMetallicRoughness.setRoughnessFactor === 'function') {
          if (mat.pbrMetallicRoughness.roughnessFactor < 0.4) {
            mat.pbrMetallicRoughness.setRoughnessFactor(0.7);
          }
        }
      };

      // 1. If merchant specified explicit target material(s)
      if (targetList.length > 0) {
        materials.forEach(mat => {
          const matName = (mat.name || '').toLowerCase().trim();
          const cleanMat = matName.replace(/[_-\s]+/g, '');
          const isMatch = targetList.some(t => {
            const cleanT = t.replace(/[_-\s]+/g, '');
            return matName === t || matName.includes(t) || t.includes(matName) ||
                   cleanMat === cleanT || cleanMat.includes(cleanT) || cleanT.includes(cleanMat);
          });
          if (isMatch) {
            matchedCount++;
            applyMatColor(mat);
          }
        });

        if (matchedCount > 0) {
          console.log(`[3D Customizer] Succès : Couleur ${cssColor} appliquée sur ${matchedCount} matériau(x) ciblé(s) [${rawTarget}]`);
          return;
        } else {
          console.warn(`[3D Customizer] Aucun matériau ne correspond à "${rawTarget}". Matériaux présents dans votre modèle 3D :`, materials.map(m => m.name));
          console.log('[3D Customizer] Bascule de secours automatique sur le maillage...');
        }
      }

      // 2. Automatic smart targeting (fallback or when target setting is left blank)
      materials.forEach(mat => {
        const matName = (mat.name || '').toLowerCase().trim();

        // Protect non-fabric parts (wheels, metal, chassis, tires, glass, screws)
        const isHardware = matName.includes('wheel') || matName.includes('tire') ||
                           matName.includes('pneu') || matName.includes('roue') ||
                           matName.includes('jante') || matName.includes('rim') ||
                           matName.includes('metal') || matName.includes('acier') ||
                           matName.includes('iron') || matName.includes('chrome') ||
                           matName.includes('glass') || matName.includes('vitre') ||
                           matName.includes('chassis') || matName.includes('cadre') ||
                           (matName.includes('frame') && !matName.includes('framboise')) ||
                           matName.includes('noir_mat') || matName.includes('rubber') ||
                           matName.includes('vis') || matName.includes('screw');

        if (isHardware) {
          return;
        }

        applyMatColor(mat);
        matchedCount++;
      });

      console.log(`[3D Customizer] Teinte automatique appliquée à ${matchedCount} matériau(x) :`, materials.map(m => m.name));
    }

    hexToRgb(hex) {
      if (!hex) return null;
      let clean = hex.replace('#', '');
      if (clean.length === 3) {
        clean = clean.split('').map(c => c + c).join('');
      }
      if (clean.length !== 6) return null;
      const num = parseInt(clean, 16);
      if (isNaN(num)) return null;
      return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255
      };
    }

    rgbToHex(r, g, b) {
      const toHex = (n) => {
        const h = Math.max(0, Math.min(255, Math.round(n))).toString(16);
        return h.length === 1 ? '0' + h : h;
      };
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    cssColorToRgb(str) {
      if (!str) return null;
      const clean = str.trim().toLowerCase();
      try {
        const dummy = document.createElement('div');
        dummy.style.color = clean;
        document.body.appendChild(dummy);
        const computed = window.getComputedStyle(dummy).color;
        document.body.removeChild(dummy);
        const m = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (m) {
          return { r: parseInt(m[1], 10), g: parseInt(m[2], 10), b: parseInt(m[3], 10) };
        }
      } catch (e) {}
      return null;
    }

    detectColorRgb(name) {
      if (!name) return null;
      const lower = name.toLowerCase().trim();
      const map = {
        // Rouges & Dérivés
        'rouge': { r: 211, g: 47, b: 47 },
        'red': { r: 211, g: 47, b: 47 },
        'bordeaux': { r: 107, g: 29, b: 47 },
        'bourgogne': { r: 107, g: 29, b: 47 },
        'grenat': { r: 107, g: 29, b: 47 },
        'cerise': { r: 184, g: 35, b: 60 },
        'cherry': { r: 184, g: 35, b: 60 },
        'rubis': { r: 168, g: 28, b: 55 },
        'carmin': { r: 150, g: 0, b: 24 },
        'framboise': { r: 199, g: 44, b: 72 },
        'raspberry': { r: 199, g: 44, b: 72 },
        'vermillon': { r: 217, g: 56, b: 30 },
        // Oranges & Coraux
        'orange': { r: 230, g: 126, b: 34 },
        'abricot': { r: 232, g: 151, b: 88 },
        'mandarine': { r: 243, g: 129, b: 40 },
        'corail': { r: 224, g: 109, b: 83 },
        'coral': { r: 224, g: 109, b: 83 },
        'saumon': { r: 242, g: 142, b: 121 },
        'salmon': { r: 242, g: 142, b: 121 },
        // Violets & Mauves
        'violet': { r: 123, g: 63, b: 141 },
        'purple': { r: 123, g: 63, b: 141 },
        'pourpre': { r: 123, g: 63, b: 141 },
        'prune': { r: 90, g: 45, b: 90 },
        'aubergine': { r: 72, g: 35, b: 60 },
        'lilas': { r: 182, g: 149, b: 192 },
        'mauve': { r: 195, g: 153, b: 189 },
        'lavande': { r: 150, g: 123, b: 182 },
        'magenta': { r: 208, g: 65, b: 126 },
        // Neutres & Clairs
        'blanc': { r: 255, g: 255, b: 255 },
        'white': { r: 255, g: 255, b: 255 },
        'neige': { r: 250, g: 250, b: 250 },
        'craie': { r: 245, g: 243, b: 238 },
        'ivoire': { r: 248, g: 245, b: 238 },
        'ivory': { r: 248, g: 245, b: 238 },
        'ecru': { r: 245, g: 240, b: 230 },
        'écru': { r: 245, g: 240, b: 230 },
        'creme': { r: 247, g: 243, b: 236 },
        'crème': { r: 247, g: 243, b: 236 },
        'cream': { r: 247, g: 243, b: 236 },
        'lin': { r: 229, g: 218, b: 203 },
        'sable': { r: 229, g: 218, b: 203 },
        'sand': { r: 229, g: 218, b: 203 },
        'beige': { r: 228, g: 216, b: 205 },
        'naturel': { r: 228, g: 216, b: 205 },
        'avoine': { r: 228, g: 216, b: 205 },
        // Roses
        'rose poudré': { r: 232, g: 180, b: 184 },
        'rose poudre': { r: 232, g: 180, b: 184 },
        'poudré': { r: 232, g: 180, b: 184 },
        'poudre': { r: 232, g: 180, b: 184 },
        'rose': { r: 232, g: 180, b: 184 },
        'blush': { r: 232, g: 180, b: 184 },
        'pink': { r: 235, g: 185, b: 190 },
        'nude': { r: 243, g: 210, b: 193 },
        'pêche': { r: 243, g: 210, b: 193 },
        'peche': { r: 243, g: 210, b: 193 },
        // Terres & Bruns
        'terracotta': { r: 200, g: 100, b: 74 },
        'brique': { r: 185, g: 85, b: 65 },
        'rouille': { r: 185, g: 85, b: 65 },
        'caramel': { r: 184, g: 115, b: 67 },
        'camel': { r: 190, g: 130, b: 75 },
        'cognac': { r: 175, g: 100, b: 55 },
        'cuivre': { r: 184, g: 115, b: 67 },
        'chocolat': { r: 92, g: 58, b: 33 },
        'marron': { r: 92, g: 58, b: 33 },
        'brown': { r: 92, g: 58, b: 33 },
        'taupe': { r: 139, g: 128, b: 118 },
        // Verts
        'sauge': { r: 156, g: 175, b: 136 },
        'sage': { r: 156, g: 175, b: 136 },
        'celadon': { r: 160, g: 185, b: 155 },
        'céladon': { r: 160, g: 185, b: 155 },
        'kaki': { r: 112, g: 130, b: 56 },
        'khaki': { r: 112, g: 130, b: 56 },
        'olive': { r: 112, g: 130, b: 56 },
        'vert': { r: 91, g: 140, b: 90 },
        'menthe': { r: 145, g: 195, b: 165 },
        'green': { r: 91, g: 140, b: 90 },
        // Bleus
        'bleu ciel': { r: 155, g: 183, b: 212 },
        'ciel': { r: 155, g: 183, b: 212 },
        'pastel': { r: 155, g: 183, b: 212 },
        'bleu roi': { r: 36, g: 81, b: 178 },
        'royal': { r: 36, g: 81, b: 178 },
        'bleu': { r: 59, g: 105, b: 120 },
        'canard': { r: 40, g: 90, b: 105 },
        'denim': { r: 59, g: 105, b: 120 },
        'marine': { r: 30, g: 45, b: 64 },
        'navy': { r: 30, g: 45, b: 64 },
        'bleu nuit': { r: 25, g: 38, b: 58 },
        'nuit': { r: 25, g: 38, b: 58 },
        'turquoise': { r: 56, g: 168, b: 157 },
        'cyan': { r: 56, g: 168, b: 157 },
        'petrole': { r: 36, g: 78, b: 90 },
        'pétrole': { r: 36, g: 78, b: 90 },
        // Jaunes & Métaux
        'jaune': { r: 212, g: 163, b: 67 },
        'yellow': { r: 212, g: 163, b: 67 },
        'moutarde': { r: 212, g: 163, b: 67 },
        'miel': { r: 215, g: 170, b: 70 },
        'ocre': { r: 200, g: 140, b: 50 },
        'gris clair': { r: 217, g: 217, b: 217 },
        'perle': { r: 217, g: 217, b: 217 },
        'gris': { r: 158, g: 158, b: 158 },
        'grey': { r: 158, g: 158, b: 158 },
        'gray': { r: 158, g: 158, b: 158 },
        'souris': { r: 158, g: 158, b: 158 },
        'anthracite': { r: 74, g: 74, b: 74 },
        'charcoal': { r: 74, g: 74, b: 74 },
        'noir': { r: 34, g: 34, b: 34 },
        'black': { r: 34, g: 34, b: 34 },
        'doré': { r: 212, g: 175, b: 55 },
        'dore': { r: 212, g: 175, b: 55 },
        'gold': { r: 212, g: 175, b: 55 },
        'argent': { r: 192, g: 192, b: 192 },
        'silver': { r: 192, g: 192, b: 192 }
      };

      for (const [key, val] of Object.entries(map)) {
        if (lower === key || lower.includes(key) || key.includes(lower)) return val;
      }
      return null;
    }

    // =========================================================================
    // EQUIPMENTS & ACCESSORIES (SWAP 3D MODEL + PRICE RECALCULATION)
    // =========================================================================
    initEquipments() {
      const equipmentCards = this.querySelectorAll('[data-equipment-card]');
      if (!equipmentCards.length) return;

      equipmentCards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          const isSelected = card.classList.contains('is-selected');

          // Single active equipment behavior (or can be toggled)
          equipmentCards.forEach(c => c.classList.remove('is-selected'));

          if (!isSelected) {
            card.classList.add('is-selected');
            const name = card.dataset.equipmentName;
            const price = parseInt(card.dataset.equipmentPrice || '0', 10);
            const model = card.dataset.equipmentModel;
            const iosModel = card.dataset.equipmentIosModel;

            this.activeEquipment = { name, price, model, iosModel };
            this.setFormEquipmentProperty(name, price);

            // Swap 3D Model to Product + Equipment
            if (model && this.has3D) {
              this.loadNew3DModel(model, iosModel);
            }
          } else {
            this.activeEquipment = null;
            this.removeFormEquipmentProperty();

            // Revert back to base product model
            if (this.has3D) {
              this.loadNew3DModel(this.baseModelSrc, this.baseIosSrc);
            }
          }

          this.updateTotalPrice();
        });
      });
    }

    loadNew3DModel(src, iosSrc) {
      if (!this.modelViewer || !src) return;
      this.showLoading();
      this.modelViewer.src = src;
      if (iosSrc) {
        this.modelViewer.setAttribute('ios-src', iosSrc);
      }
    }

    showLoading() {
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('is-loading');
      }
    }

    hideLoading() {
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.remove('is-loading');
      }
    }

    setFormEquipmentProperty(name, price) {
      if (!this.form) return;
      let propInput = this.form.querySelector('input[name="properties[Équipement inclus]"]');
      if (!propInput) {
        propInput = document.createElement('input');
        propInput.type = 'hidden';
        propInput.name = 'properties[Équipement inclus]';
        this.form.appendChild(propInput);
      }
      propInput.value = `${name} (+${this.formatMoney(price)})`;
    }

    removeFormEquipmentProperty() {
      if (!this.form) return;
      const propInput = this.form.querySelector('input[name="properties[Équipement inclus]"]');
      if (propInput) propInput.remove();
    }

    // =========================================================================
    // PRICE RECALCULATION & FORMATTING
    // =========================================================================
    updateTotalPrice() {
      let total = this.basePrice;
      let compareTotal = this.comparePrice;
      if (this.activeEquipment) {
        total += this.activeEquipment.price;
        if (compareTotal > 0) {
          compareTotal += this.activeEquipment.price;
        }
      }

      const formatted = this.formatMoney(total);
      if (this.priceCurrent) this.priceCurrent.textContent = formatted;
      if (this.stickyPrice) this.stickyPrice.textContent = formatted;

      if (this.priceCompare && this.priceBadge) {
        if (compareTotal > total) {
          const savingCents = compareTotal - total;
          this.priceCompare.textContent = this.formatMoney(compareTotal);
          this.priceCompare.style.display = '';
          this.priceBadge.textContent = `Vous économisez ${this.formatMoney(savingCents)}`;
          this.priceBadge.style.display = '';
        } else {
          this.priceCompare.style.display = 'none';
          this.priceBadge.style.display = 'none';
        }
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return `${amount} ${this.currencySymbol}`;
    }

    // =========================================================================
    // QUANTITY SELECTOR
    // =========================================================================
    initQuantity() {
      const minusBtn = this.querySelector('[data-action="qty-minus"]');
      const plusBtn = this.querySelector('[data-action="qty-plus"]');
      const input = this.querySelector('.product-3d__qty-input');

      if (!input) return;

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 1;
          if (val > 1) input.value = val - 1;
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 1;
          input.value = val + 1;
        });
      }
    }

    // =========================================================================
    // THUMBNAILS FOR 2D GALLERY & FALLBACK (VERTICAL DRAG-TO-SCROLL)
    // =========================================================================
    initThumbnails() {
      const thumbs = this.querySelectorAll('[data-thumb-target]');
      const thumbsTrack = this.querySelector('.product-3d__thumbs');
      const mainImg = this.querySelector('.product-3d__gallery-main img');
      const mainVideo = this.querySelector('.product-3d__gallery-main video');

      let isDraggingThumb = false;

      if (thumbsTrack) {
        let isDown = false;
        let startY = 0;
        let scrollTop = 0;

        thumbsTrack.addEventListener('mousedown', (e) => {
          isDown = true;
          isDraggingThumb = false;
          startY = e.pageY - thumbsTrack.offsetTop;
          scrollTop = thumbsTrack.scrollTop;
        });

        thumbsTrack.addEventListener('mouseleave', () => {
          isDown = false;
        });

        thumbsTrack.addEventListener('mouseup', () => {
          isDown = false;
          setTimeout(() => { isDraggingThumb = false; }, 50);
        });

        thumbsTrack.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const y = e.pageY - thumbsTrack.offsetTop;
          const walk = (y - startY) * 1.4;
          if (Math.abs(walk) > 5) {
            isDraggingThumb = true;
            e.preventDefault();
            thumbsTrack.scrollTop = scrollTop - walk;
          }
        });
      }

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          if (isDraggingThumb) return;

          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');

          const imgSrc = thumb.dataset.imgSrc;
          const mediaType = thumb.dataset.mediaType;

          if (mediaType === 'image' && mainImg && imgSrc) {
            mainImg.src = imgSrc;
            mainImg.style.display = 'block';
            if (mainVideo) mainVideo.style.display = 'none';
          }

          // Center active thumbnail smoothly in the vertical column
          if (thumbsTrack) {
            const thumbTop = thumb.offsetTop;
            const thumbHeight = thumb.offsetHeight;
            const trackHeight = thumbsTrack.clientHeight;
            thumbsTrack.scrollTo({
              top: thumbTop - (trackHeight / 2) + (thumbHeight / 2),
              behavior: 'smooth'
            });
          }
        });
      });
    }

    // =========================================================================
    // STICKY ADD TO CART
    // =========================================================================
    initStickyCart() {
      if (!this.stickyBar || !this.mainAddBtn) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            this.stickyBar.classList.add('is-visible');
          } else {
            this.stickyBar.classList.remove('is-visible');
          }
        });
      }, { threshold: 0.1 });

      observer.observe(this.mainAddBtn);

      const stickyBtn = this.stickyBar.querySelector('.product-3d__sticky-btn');
      if (stickyBtn && this.mainAddBtn) {
        stickyBtn.addEventListener('click', () => {
          this.mainAddBtn.click();
        });
      }
    }

    // =========================================================================
    // DYNAMIC DELIVERY ESTIMATOR (FRENCH LOCALE & CUSTOMIZABLE DELAYS)
    // =========================================================================
    initDeliveryEstimator() {
      const deliveryBoxes = this.querySelectorAll('.product-3d__delivery-box');
      if (!deliveryBoxes.length) return;

      deliveryBoxes.forEach(box => {
        const dateTarget = box.querySelector('[data-delivery-date]');
        if (!dateTarget) return;

        const minDays = parseInt(box.dataset.deliveryMin || '5', 10);
        const maxDays = parseInt(box.dataset.deliveryMax || '8', 10);
        const skipWeekends = box.dataset.skipWeekends !== 'false';

        const addDays = (startDate, daysToAdd, skip) => {
          let date = new Date(startDate);
          let added = 0;
          while (added < daysToAdd) {
            date.setDate(date.getDate() + 1);
            if (skip) {
              const day = date.getDay();
              if (day !== 0 && day !== 6) { // 0 = Dimanche, 6 = Samedi
                added++;
              }
            } else {
              added++;
            }
          }
          return date;
        };

        const now = new Date();
        const minDate = addDays(now, minDays, skipWeekends);
        const maxDate = addDays(now, maxDays, skipWeekends);

        const sameMonth = minDate.getMonth() === maxDate.getMonth() && minDate.getFullYear() === maxDate.getFullYear();
        const minOptions = sameMonth
          ? { weekday: 'long', day: 'numeric' }
          : { weekday: 'long', day: 'numeric', month: 'long' };
        const maxOptions = { weekday: 'long', day: 'numeric', month: 'long' };
        const minStr = minDate.toLocaleDateString('fr-FR', minOptions);
        const maxStr = maxDate.toLocaleDateString('fr-FR', maxOptions);

        dateTarget.textContent = `${minStr} et ${maxStr}`;
      });
    }

    // =========================================================================
    // URGENCY OFFER END DATE CALCULATOR (STOCK FAIBLE & FIN D'OFFRE)
    // =========================================================================
    initUrgencyDeadlines() {
      const deadlineEls = this.querySelectorAll('[data-urgency-deadline]');
      if (!deadlineEls.length) return;

      deadlineEls.forEach(deadlineEl => {
        const parentBox = deadlineEl.closest('[data-urgency-box]');
        const rawDays = deadlineEl.dataset.offerDays || (parentBox && parentBox.dataset.offerDays) || '3';
        const daysToAdd = parseInt(rawDays, 10) || 3;
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + daysToAdd);

        const options = { weekday: 'long', day: 'numeric', month: 'long' };
        deadlineEl.textContent = targetDate.toLocaleDateString('fr-FR', options);
      });
    }

    // =========================================================================
    // LOOX REVIEWS & DRAWER / ACCORDION AUTO-OPEN TRIGGER
    // =========================================================================
    initLooxTriggers() {
      const triggers = document.querySelectorAll('[data-loox-trigger], a[href="#looxReviews"], .product-3d__loox-rating-link');
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          const looxReviews = document.getElementById('looxReviews');
          if (!looxReviews) return;

          e.preventDefault();

          // If the reviews widget is inside a collapsible drawer / accordion (<details>), open it!
          const parentDetails = looxReviews.closest('details');
          if (parentDetails) {
            parentDetails.open = true;
          }

          // Smooth scroll to the reviews widget or open drawer
          const scrollTarget = parentDetails || looxReviews;
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }
  }

  customElements.define('product-3d-customizer', Product3DCustomizer);
}
